import {
  BUSINESS_ENTITY_KINDS,
  NON_TRUST_CHARITABLE_ENTITY_KINDS,
  PERSONAL_FAMILY_ACCOUNT_KINDS,
  TRUST_ENTITY_KINDS,
} from '@/modules/entities/entities.constants';
import {
  isEstateEntitySuggestionReviewable,
  isSuggestionForPrimaryDeathOrder,
} from '@/modules/estate/estateAIOnboarding/estateOnboarding.utils';
import {
  EstatePage_EntityFragment,
  EstatePage_TestamentaryEntityFragment,
  EstatePage_UnacknowledgedAiSuggestionFragment,
} from '@/modules/estate/graphql/EstatePageData.generated';
import { AiSuggestionKind, EntityStage } from '@/types/schema';

import { ReviewSuggestionCountFragment } from './ReviewSuggestions.generated';

export enum ReviewSuggestionCategory {
  DirectHoldings = 'direct-holdings',
  Trusts = 'trusts',
  Businesses = 'businesses',
}

export const REVIEW_SUGGESTION_CATEGORY_LABELS: Record<
  ReviewSuggestionCategory,
  string
> = {
  [ReviewSuggestionCategory.DirectHoldings]: 'Direct holdings',
  [ReviewSuggestionCategory.Trusts]: 'Trusts',
  [ReviewSuggestionCategory.Businesses]: 'Businesses',
};

export const REVIEW_SUGGESTION_CATEGORY_ORDER = Object.values(
  ReviewSuggestionCategory
);

export type ReviewSuggestionCounts = Record<ReviewSuggestionCategory, number>;

export function getReviewSuggestionCounts(
  suggestions: ReviewSuggestionCountFragment[],
  primaryDeathOrderClientId?: string
): ReviewSuggestionCounts {
  const counts: ReviewSuggestionCounts = {
    [ReviewSuggestionCategory.DirectHoldings]: 0,
    [ReviewSuggestionCategory.Trusts]: 0,
    [ReviewSuggestionCategory.Businesses]: 0,
  };

  suggestions.forEach((suggestion) => {
    if (suggestion.kind === AiSuggestionKind.OnboardingTestamentaryEntity) {
      counts[ReviewSuggestionCategory.Trusts] += 1;
      return;
    }

    if (suggestion.kind === AiSuggestionKind.OnboardingEntity) {
      const entityKind = suggestion.onboardingEntity?.entityKind;
      if (
        entityKind &&
        [...TRUST_ENTITY_KINDS, ...NON_TRUST_CHARITABLE_ENTITY_KINDS].includes(
          entityKind
        )
      ) {
        counts[ReviewSuggestionCategory.Trusts] += 1;
      } else if (entityKind && BUSINESS_ENTITY_KINDS.includes(entityKind)) {
        counts[ReviewSuggestionCategory.Businesses] += 1;
      }
      return;
    }

    if (
      suggestion.entity &&
      !isEstateEntitySuggestionReviewable(
        suggestion,
        suggestion.entity.kind,
        primaryDeathOrderClientId
      )
    ) {
      return;
    }

    if (
      suggestion.testamentaryEntity &&
      !isSuggestionForPrimaryDeathOrder(suggestion, primaryDeathOrderClientId)
    ) {
      return;
    }

    if (suggestion.testamentaryEntity) {
      counts[ReviewSuggestionCategory.Trusts] += 1;
      return;
    }

    const entity = suggestion.entity;
    if (!entity) return;

    if (PERSONAL_FAMILY_ACCOUNT_KINDS.includes(entity.kind)) {
      counts[ReviewSuggestionCategory.DirectHoldings] += 1;
      return;
    }

    if (![EntityStage.Active, EntityStage.Completed].includes(entity.stage)) {
      return;
    }

    if (
      [...TRUST_ENTITY_KINDS, ...NON_TRUST_CHARITABLE_ENTITY_KINDS].includes(
        entity.kind
      )
    ) {
      counts[ReviewSuggestionCategory.Trusts] += 1;
    } else if (BUSINESS_ENTITY_KINDS.includes(entity.kind)) {
      counts[ReviewSuggestionCategory.Businesses] += 1;
    }
  });

  return counts;
}

type SuggestionsByEntityID = Record<
  string,
  EstatePage_UnacknowledgedAiSuggestionFragment[] | undefined
>;

interface GetEstateReviewSuggestionsInput {
  entities: EstatePage_EntityFragment[];
  testamentaryEntities: EstatePage_TestamentaryEntityFragment[];
  suggestionsByEntityID: SuggestionsByEntityID;
  primaryDeathOrderClientId?: string;
}

export function getEstateReviewSuggestions({
  entities,
  testamentaryEntities,
  suggestionsByEntityID,
  primaryDeathOrderClientId,
}: GetEstateReviewSuggestionsInput) {
  const entityKindsByID = new Map(
    entities.map((entity) => [entity.id, entity.kind])
  );
  const pendingSuggestionsFor = (id: string) =>
    (suggestionsByEntityID[id] ?? []).filter((suggestion) => {
      if (suggestion.acceptanceStatus) return false;

      const entityKind = entityKindsByID.get(id);
      if (entityKind) {
        return isEstateEntitySuggestionReviewable(
          suggestion,
          entityKind,
          primaryDeathOrderClientId
        );
      }

      return isSuggestionForPrimaryDeathOrder(
        suggestion,
        primaryDeathOrderClientId
      );
    });

  const directHoldings = entities.filter(
    (entity) =>
      PERSONAL_FAMILY_ACCOUNT_KINDS.includes(entity.kind) &&
      pendingSuggestionsFor(entity.id).length > 0
  );
  const trusts = entities.filter(
    (entity) =>
      [EntityStage.Active, EntityStage.Completed].includes(entity.stage) &&
      [...TRUST_ENTITY_KINDS, ...NON_TRUST_CHARITABLE_ENTITY_KINDS].includes(
        entity.kind
      ) &&
      pendingSuggestionsFor(entity.id).length > 0
  );
  const businesses = entities.filter(
    (entity) =>
      [EntityStage.Active, EntityStage.Completed].includes(entity.stage) &&
      BUSINESS_ENTITY_KINDS.includes(entity.kind) &&
      pendingSuggestionsFor(entity.id).length > 0
  );
  const subtrusts = testamentaryEntities.filter(
    (entity) => pendingSuggestionsFor(entity.id).length > 0
  );

  const countSuggestions = (entityIDs: string[]) =>
    entityIDs.reduce(
      (count, id) => count + pendingSuggestionsFor(id).length,
      0
    );

  return {
    directHoldings,
    trusts,
    businesses,
    subtrusts,
    counts: {
      directHoldings: countSuggestions(directHoldings.map(({ id }) => id)),
      trusts: countSuggestions([
        ...trusts.map(({ id }) => id),
        ...subtrusts.map(({ id }) => id),
      ]),
      businesses: countSuggestions(businesses.map(({ id }) => id)),
    },
  };
}

export function getInitiallyVisibleCategories(counts: ReviewSuggestionCounts) {
  return REVIEW_SUGGESTION_CATEGORY_ORDER.filter(
    (category) => counts[category] > 0
  );
}
